import {
  useEffect,
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type CSSProperties,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";
import { createPortal } from "react-dom";
import { ApiError } from "../../api/client";
import type { PuzzleStatus, Role } from "../../api/schemas";
import styles from "./ui.module.css";

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(" ");

// ---------------------------------------------------------------- buttons

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md";
  icon?: ReactNode;
  busy?: boolean;
  shortcut?: string;
};

export function Button({ variant = "secondary", size = "md", icon, busy, shortcut, className, children, disabled, ...rest }: ButtonProps) {
  return (
    <button
      type="button"
      className={cx(styles.button, className)}
      data-variant={variant}
      data-size={size}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      {...rest}
    >
      {busy ? <span className={styles.spinner} aria-hidden /> : icon}
      {children ? <span>{children}</span> : null}
      {shortcut ? <kbd className={styles.buttonKbd}>{shortcut}</kbd> : null}
    </button>
  );
}

// ---------------------------------------------------------------- chips

export function Chip({ children, tone = "neutral", title }: { children: ReactNode; tone?: string; title?: string }) {
  return (
    <span className={styles.chip} data-tone={tone} title={title}>
      {children}
    </span>
  );
}

const STATUS_TONE: Record<PuzzleStatus, string> = {
  draft: "neutral",
  approved: "success",
  published: "info",
  rejected: "danger",
  retired: "muted",
};

export function StatusChip({ status }: { status: PuzzleStatus | null | undefined }) {
  if (!status) return <Chip tone="muted">empty</Chip>;
  return (
    <span className={styles.chip} data-tone={STATUS_TONE[status]} data-status={status}>
      <span className={styles.chipDot} aria-hidden />
      {status}
    </span>
  );
}

export function RoleChip({ role }: { role: Role }) {
  return (
    <span className={styles.chip} data-tone={role === "admin" ? "accent" : role === "editor" ? "info" : "neutral"}>
      {role}
    </span>
  );
}

export function CategoryTag({ category }: { category: string | null | undefined }) {
  if (!category) return <span className={styles.muted}>—</span>;
  return (
    <span className={styles.category} data-cat={category}>
      <span className={styles.categoryDot} aria-hidden />
      {category}
    </span>
  );
}

export function TierDots({ tier, max = 5 }: { tier: number | null | undefined; max?: number }) {
  const t = tier ?? 0;
  return (
    <span className={styles.tier} role="img" aria-label={tier ? `Tier ${tier} of ${max}` : "No tier"}>
      {Array.from({ length: max }, (_, i) => (
        <span key={i} data-on={i < t || undefined} />
      ))}
    </span>
  );
}

// ---------------------------------------------------------------- layout blocks

export function Card({
  title,
  actions,
  children,
  className,
  padded = true,
  style,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  className?: string;
  padded?: boolean;
  style?: CSSProperties;
}) {
  return (
    <section className={cx(styles.card, !padded && styles.cardFlush, className)} style={style}>
      {title || actions ? (
        <header className={styles.cardHead}>
          {title ? <h2 className={styles.cardTitle}>{title}</h2> : <span />}
          {actions ? <div className={styles.cardActions}>{actions}</div> : null}
        </header>
      ) : null}
      {children}
    </section>
  );
}

export function PageHeader({ title, meta, actions }: { title: ReactNode; meta?: ReactNode; actions?: ReactNode }) {
  return (
    <div className={styles.pageHeader}>
      <div className={styles.pageHeaderText}>
        <h1 className={styles.pageTitle}>{title}</h1>
        {meta ? <div className={styles.pageMeta}>{meta}</div> : null}
      </div>
      {actions ? <div className={styles.pageActions}>{actions}</div> : null}
    </div>
  );
}

export function Stat({ label, value, sub }: { label: ReactNode; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className={styles.stat}>
      <span className="monoLabel">{label}</span>
      <span className={styles.statValue}>{value}</span>
      {sub ? <span className={styles.statSub}>{sub}</span> : null}
    </div>
  );
}

// ---------------------------------------------------------------- dot-matrix readout (numerals only)

const GLYPHS: Record<string, string[]> = {
  "0": ["01110", "10001", "10011", "10101", "11001", "10001", "01110"],
  "1": ["00100", "01100", "10100", "00100", "00100", "00100", "01110"],
  "2": ["01110", "10001", "00001", "00010", "00100", "01000", "11111"],
  "3": ["11111", "00010", "00100", "00010", "00001", "10001", "01110"],
  "4": ["00010", "00110", "01010", "10010", "11111", "00010", "00010"],
  "5": ["11111", "10000", "11110", "00001", "00001", "10001", "01110"],
  "6": ["00110", "01000", "10000", "11110", "10001", "10001", "01110"],
  "7": ["11111", "00001", "00010", "00100", "01000", "01000", "01000"],
  "8": ["01110", "10001", "10001", "01110", "10001", "10001", "01110"],
  "9": ["01110", "10001", "10001", "01111", "00001", "00010", "01100"],
  ":": ["00", "00", "11", "11", "00", "11", "11"],
  ".": ["00", "00", "00", "00", "00", "11", "11"],
  "-": ["000", "000", "000", "111", "000", "000", "000"],
  " ": ["00", "00", "00", "00", "00", "00", "00"],
};

/** Aurora dot-matrix numerals (tokens.dotMatrix: 5×7 cells, pitch 1.62). */
export function DotReadout({ value, dot = 7, label }: { value: string; dot?: number; label?: string }) {
  const pitch = dot * 1.62;
  const cells: { x: number; y: number }[] = [];
  let col = 0;
  const chars = [...value];
  chars.forEach((ch, gi) => {
    const g = GLYPHS[ch] ?? GLYPHS[" "]!;
    g.forEach((row, r) =>
      [...row].forEach((bit, c) => {
        if (bit === "1") cells.push({ x: (col + c) * pitch + dot / 2, y: r * pitch + dot / 2 });
      }),
    );
    col += g[0]!.length + (gi === chars.length - 1 ? 0 : 1);
  });
  const w = Math.max(dot, (col - 1) * pitch + dot);
  const h = 6 * pitch + dot;
  return (
    <svg className={styles.readout} width={w} height={h} viewBox={`0 0 ${w} ${h}`} role="img" aria-label={label ?? value}>
      {cells.map((c, i) => (
        <circle key={i} cx={c.x} cy={c.y} r={dot / 2} />
      ))}
    </svg>
  );
}

// ---------------------------------------------------------------- KPI tiles (emissive)

export function KpiTile({
  label,
  value,
  unit,
  palette,
  foot,
  href,
}: {
  label: ReactNode;
  value: string;
  unit?: string;
  palette: string;
  foot?: ReactNode;
  href?: string;
}) {
  const body = (
    <>
      <div className={styles.kpiLabel}>{label}</div>
      <div className={styles.kpiValue}>
        <DotReadout value={value} dot={6.5} />
        {unit ? <span className={styles.kpiUnit}>{unit}</span> : null}
      </div>
      {foot ? <div className={styles.kpiFoot}>{foot}</div> : null}
    </>
  );
  const cls = cx(styles.kpi, `tile-${palette}`);
  return href ? (
    <a href={href} className={cls}>
      {body}
    </a>
  ) : (
    <div className={cls}>{body}</div>
  );
}

// ---------------------------------------------------------------- form controls

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <label className={styles.field}>
      <span className="monoLabel">{label}</span>
      {children}
      {hint ? <span className={styles.fieldHint}>{hint}</span> : null}
    </label>
  );
}

export function TextInput({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx(styles.input, className)} {...rest} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cx(styles.input, styles.select, className)} {...rest}>
      {children}
    </select>
  );
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
  label,
  disabled,
}: {
  value: T;
  options: { value: T; label: ReactNode; disabled?: boolean; title?: string }[];
  onChange: (v: T) => void;
  label: string;
  /** Read-only: every option is disabled (the checked one stays shown). */
  disabled?: boolean;
}) {
  return (
    <div className={styles.segmented} role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          className={styles.segment}
          disabled={disabled || o.disabled}
          title={o.title}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- states

export function Skeleton({ height = 16, width = "100%", radius }: { height?: number | string; width?: number | string; radius?: string }) {
  return <span className={styles.skeleton} style={{ height, width, borderRadius: radius }} aria-hidden />;
}

export function Loading({ label = "Loading", rows = 6 }: { label?: string; rows?: number }) {
  return (
    <div className={styles.loading} role="status" aria-live="polite">
      <span className="visually-hidden">{label}…</span>
      {Array.from({ length: rows }, (_, i) => (
        <Skeleton key={i} height={i === 0 ? 22 : 14} width={i === 0 ? "38%" : `${92 - ((i * 17) % 40)}%`} />
      ))}
    </div>
  );
}

export function EmptyState({ title, children, action }: { title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className={styles.empty} role="status">
      <div className={styles.emptyMark} aria-hidden>
        <span />
        <span />
        <span />
      </div>
      <div className={styles.emptyTitle}>{title}</div>
      {children ? <div className={styles.emptyText}>{children}</div> : null}
      {action}
    </div>
  );
}

export function ErrorState({ error, onRetry, title = "Couldn't load this" }: { error: unknown; onRetry?: () => void; title?: string }) {
  const code = error instanceof ApiError ? error.code : "error";
  const status = error instanceof ApiError && error.status ? ` · HTTP ${error.status}` : "";
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className={styles.error} role="alert">
      <div className={styles.errorHead}>
        <span className={styles.errorDot} aria-hidden />
        <span className={styles.errorTitle}>{title}</span>
        <span className="monoLabel">
          {code}
          {status}
        </span>
      </div>
      <pre className={styles.errorMessage}>{message}</pre>
      {onRetry ? (
        <Button size="sm" onClick={onRetry}>
          Try again
        </Button>
      ) : null}
    </div>
  );
}

export function InlineError({ error }: { error: unknown }) {
  if (!error) return null;
  return (
    <div className={styles.inlineError} role="alert">
      {error instanceof ApiError ? <span className="mono">{error.code}</span> : null}
      <span>{error instanceof Error ? error.message : String(error)}</span>
    </div>
  );
}

// ---------------------------------------------------------------- dialogs

export function Dialog({
  open,
  title,
  children,
  footer,
  onClose,
  width = 460,
}: {
  open: boolean;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  width?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    const node = ref.current;
    const focusables = () =>
      Array.from(node?.querySelectorAll<HTMLElement>("button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex='-1'])") ?? []);
    (node?.querySelector<HTMLElement>("[data-autofocus]") ?? focusables()[0])?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onCloseRef.current();
      }
      if (e.key === "Tab") {
        const items = focusables();
        if (!items.length) return;
        const first = items[0]!;
        const last = items[items.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("keydown", onKey, true);
      previous?.focus?.();
    };
  }, [open]);

  if (!open) return null;
  return createPortal(
    <div className={styles.overlay} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby={titleId} style={{ width }}>
        <h2 id={titleId} className={styles.dialogTitle}>
          {title}
        </h2>
        <div className={styles.dialogBody}>{children}</div>
        {footer ? <div className={styles.dialogFooter}>{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  tone = "primary",
  busy,
  error,
  confirmDisabled,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: ReactNode;
  children: ReactNode;
  confirmLabel: string;
  tone?: "primary" | "danger";
  busy?: boolean;
  error?: unknown;
  /** Keeps the confirm button disabled (e.g. until a typed confirmation matches). */
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Dialog
      open={open}
      title={title}
      onClose={onCancel}
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={busy}>
            Cancel
          </Button>
          <Button variant={tone} onClick={onConfirm} busy={busy} disabled={confirmDisabled} data-autofocus>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
      <InlineError error={error} />
    </Dialog>
  );
}

export { cx };
